import { FilterStateType, WorklistFiltersType } from 'domains/worklist/WorklistFilters/types';
import { CustomWorklist, CustomWorklistFilter, DefaultCustomWorklist } from 'generated/graphql';
import { WorklistSurface } from '../types';
import { isProvidedCannedWorklist } from '../utils/isProvidedCannedWorklist';

/**
 *  This function converts an array of CustomWorklistFilter objects into a FilterStateType object.
 *
 *      [{name: 'modality', values: ['CT', 'MR']}, {name: 'examStatus', values: ['PENDING']}]
 *
 *  will be converted to:
 *
 *      { modality: ['CT', 'MR'], status: ['PENDING'] }
 *
 * This will happen for any type of filter
 **/
export const formatCustomWorklistFilters = (filters: ReadonlyArray<CustomWorklistFilter>) => {
  return filters.reduce((acc: FilterStateType, filter: CustomWorklistFilter) => {
    if (filter == null || filter.name == null || filter.values == null) {
      return acc;
    }

    acc[filter.name as WorklistFiltersType] = [...filter.values];
    return acc;
  }, {} as FilterStateType);
};

/**
 * This function filters out invalid custom worklists based on the provided criteria.
 * It ensures that only valid custom worklists are returned, specifically those that
 * have a valid worklistViewID when the surface is CUSTOM_WORKLIST.
 *
 * @param customWorklists - An array of DefaultCustomWorklist objects to be validated.
 * @param allWorklists - A Map containing all available CustomWorklist objects.
 * @returns An array of valid DefaultCustomWorklist objects.
 */
export const getValidCustomWorklists = (
  customWorklists: DefaultCustomWorklist[],
  allWorklists: Map<string, CustomWorklist>
): DefaultCustomWorklist[] => {
  return customWorklists
    .filter((worklist) => {
      // Check if this is a legacy worklist, not a custom worklist
      if (
        worklist.surface !== WorklistSurface.CUSTOM_WORKLIST &&
        worklist?.worklistViewID?.length === 0
      ) {
        return true;
      }

      return (
        worklist.surface === WorklistSurface.CUSTOM_WORKLIST &&
        Array.isArray(worklist.worklistViewID) &&
        worklist.worklistViewID.length > 0 &&
        worklist.worklistViewID?.every((id) => id != null && allWorklists.has(id))
      );
    })
    .slice(0, 1);
};

export const getCustomWorklistNames = (
  customWorklistIDs: string[],
  customWorklists: Map<string, CustomWorklist>
): (string | null)[] => {
  if (
    customWorklistIDs == null ||
    customWorklistIDs.length === 0 ||
    customWorklists == null ||
    customWorklists.size === 0
  ) {
    return [];
  }

  return customWorklistIDs.map((id) => customWorklists.get(id)?.name ?? null);
};

/**
 * Checks if a worklist is a protected canned worklist:
 *  - My Queue
 *  - Assigned to Me
 *  - Archive
 *
 * Protected worklists should not allow CRUD operations.
 *
 * @param worklistName - The name of the worklist to check
 * @returns true if the worklist is protected from user modifications, false otherwise
 */
export const isProtectedWorklist = (worklistName: string): boolean => {
  return isProvidedCannedWorklist({
    currentSurface: WorklistSurface.CUSTOM_WORKLIST,
    comparatorSurface: [WorklistSurface.MY_QUEUE, WorklistSurface.ARCHIVE],
    customWorklistName: worklistName,
  });
};
